import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { composeUserName, splitLegacyUserName } from "@/lib/personName";

const namePart = (label: string) =>
  z
    .string()
    .min(2, `${label} must contain at least 2 characters`)
    .max(50, `${label} must not exceed 50 characters`)
    .refine((value) => value.trim().length >= 2, {
      message: `${label} cannot consist only of spaces`,
    });

const profileNameSchema = z.object({
  first_name: namePart("First name"),
  last_name: namePart("Last name"),
});
/** Named `…Values`, not `ProfileNameForm`, so the type never shadows the exported component. */
type ProfileNameValues = z.infer<typeof profileNameSchema>;

/**
 * AE-207: the only editable part of the profile.
 *
 * `first_name` / `last_name` are the stored source of truth. When they are still NULL —
 * a row the migration backfill has not reached — the inputs seed from `user_name` using
 * the same first-space split as the backfill, so the user sees the same values either way.
 */
export const ProfileNameForm = ({
  firstName,
  lastName,
  userName,
}: {
  firstName?: string | null;
  lastName?: string | null;
  userName?: string | null;
}) => {
  const { updateUserName } = useAuth();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const legacy = splitLegacyUserName(userName);
  const seededFirst = (firstName ?? "").trim() || legacy.firstName;
  const seededLast = (lastName ?? "").trim() || (firstName ? "" : legacy.lastName);
  const displayName = composeUserName(seededFirst, seededLast);

  const form = useForm<ProfileNameValues>({
    resolver: zodResolver(profileNameSchema),
    defaultValues: { first_name: seededFirst, last_name: seededLast },
    mode: "onSubmit",
  });

  const startEditing = () => {
    form.reset({ first_name: seededFirst, last_name: seededLast });
    setIsEditing(true);
  };

  const onSubmit = async (values: ProfileNameValues) => {
    try {
      await updateUserName({
        firstName: values.first_name.trim(),
        lastName: values.last_name.trim(),
      });
      setIsEditing(false);
      toast({ title: "Name updated" });
    } catch {
      toast({
        title: "Could not update your name",
        description: "Please try again, or contact support if the problem continues.",
        variant: "destructive",
      });
    }
  };

  if (!isEditing) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
        <div>
          <p className="font-inter text-xs uppercase tracking-wide text-gray-500">Name</p>
          <p className="mt-1 font-inter text-base text-gray-900">{displayName || "—"}</p>
        </div>

        <Button type="button" variant="secondary" size="sm" onClick={startEditing}>
          Edit
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 border-b border-gray-100 pb-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label
            htmlFor="profile-first-name"
            className="block font-inter text-xs uppercase tracking-wide text-gray-500"
          >
            First name
          </label>
          <Input id="profile-first-name" autoFocus {...form.register("first_name")} />
          {form.formState.errors.first_name && (
            <p className="font-inter text-xs text-red-600">{form.formState.errors.first_name.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="profile-last-name"
            className="block font-inter text-xs uppercase tracking-wide text-gray-500"
          >
            Last name
          </label>
          <Input id="profile-last-name" {...form.register("last_name")} />
          {form.formState.errors.last_name && (
            <p className="font-inter text-xs text-red-600">{form.formState.errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
